import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import PasswordInput from '@/components/PasswordInput';
import { toast } from 'sonner';
import { GraduationCap, BookOpen, Lock, User, Mail, ArrowRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage() {
  const [role, setRole] = useState('student');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const { login, user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  if (user) { navigate('/dashboard'); return null; }

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(username, password, role);
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al iniciar sesion');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      toast.success('Se ha enviado un correo con la contrasena temporal');
      setShowForgot(false);
      setForgotEmail('');
    } catch {
      toast.error('Error al enviar el correo');
    }
  };

  return (
    <div className="login-bg flex min-h-screen" data-testid="login-page">
      {/* Left Visual Panel */}
      <div className="hidden lg:flex w-1/2 relative flex-col items-center justify-center p-12 z-10">
        <div className="absolute inset-0 flag-stripe-us" />
        <div className="absolute inset-0 flag-stripe-uk" style={{ opacity: 0.04 }} />
        <div className="relative z-10 text-center space-y-8 animate-fade-in">
          <div className="mb-6">
            <h1 className="text-6xl font-black text-white neon-glow tracking-tight" style={{ fontFamily: 'Montserrat' }}>
              Active<span className="text-red-400">English</span>
            </h1>
          </div>
          <div className="space-y-4">
            <p className="text-2xl text-blue-200 font-semibold italic" style={{ fontFamily: 'Montserrat' }}>
              "{t('slogan1')}"
            </p>
            <p className="text-xl text-slate-300 font-medium" style={{ fontFamily: 'Montserrat' }}>
              "{t('slogan2')}"
            </p>
          </div>
          <div className="flex justify-center gap-6 mt-10">
            <div className="w-16 h-10 rounded-md overflow-hidden shadow-lg" style={{ background: 'linear-gradient(180deg, #B22234 0%, #B22234 15%, #FFF 15%, #FFF 23%, #B22234 23%, #B22234 38%, #FFF 38%, #FFF 46%, #3C3B6E 46%, #3C3B6E 100%)' }} />
            <div className="w-16 h-10 rounded-md overflow-hidden shadow-lg" style={{ background: 'linear-gradient(45deg, #012169 0%, #012169 33%, #C8102E 33%, #C8102E 40%, #FFFFFF 40%, #FFFFFF 50%, #C8102E 50%, #C8102E 60%, #012169 60%, #012169 100%)' }} />
          </div>
        </div>
      </div>

      {/* Right Form Panel */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-12 z-10">
        <div className="glass-card p-8 sm:p-10 w-full max-w-md animate-slide-up">
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-8">
            <h1 className="text-4xl font-black text-white neon-glow" style={{ fontFamily: 'Montserrat' }}>
              Active<span className="text-red-400">English</span>
            </h1>
            <p className="text-blue-200 text-sm mt-2 italic">"{t('slogan1')}"</p>
          </div>

          <h2 className="text-2xl font-bold text-white mb-8 text-center" style={{ fontFamily: 'Montserrat' }}>
            {t('login_title')}
          </h2>

          {/* Role Selector */}
          <div className="flex gap-3 mb-8" data-testid="role-selector">
            <button
              onClick={() => setRole('student')}
              data-testid="role-student-btn"
              className={`flex-1 py-3 px-4 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2 ${
                role === 'student'
                  ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/30'
                  : 'bg-white/10 text-slate-300 hover:bg-white/15'
              }`}
              style={{ fontFamily: 'Montserrat' }}
            >
              <GraduationCap className="w-5 h-5" />
              {t('student')}
            </button>
            <button
              onClick={() => setRole('teacher')}
              data-testid="role-teacher-btn"
              className={`flex-1 py-3 px-4 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2 ${
                role === 'teacher'
                  ? 'bg-red-500 text-white shadow-lg shadow-red-500/30'
                  : 'bg-white/10 text-slate-300 hover:bg-white/15'
              }`}
              style={{ fontFamily: 'Montserrat' }}
            >
              <BookOpen className="w-5 h-5" />
              {t('teacher')}
            </button>
          </div>

          {/* Login Form */}
          <form onSubmit={handleLogin} className="space-y-5">
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm font-medium">{t('username')}</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  data-testid="login-username-input"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder={t('username')}
                  className="pl-10 bg-white/10 border-white/15 text-white placeholder:text-slate-500 h-12 rounded-xl"
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm font-medium">{t('password')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 z-10" />
                <PasswordInput
                  data-testid="login-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('password')}
                  className="pl-10 pr-24 bg-white/10 border-white/15 text-white placeholder:text-slate-500 h-12 rounded-xl"
                  required
                />
              </div>
            </div>

            <Button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-xl font-bold text-base transition-all"
              style={{
                fontFamily: 'Montserrat',
                background: role === 'student' ? '#2563EB' : '#DC2626',
                boxShadow: role === 'student' ? '0 8px 24px -4px rgba(37,99,235,0.4)' : '0 8px 24px -4px rgba(220,38,38,0.4)'
              }}
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <span className="flex items-center gap-2">{t('login_button')} <ArrowRight className="w-4 h-4" /></span>
              )}
            </Button>
          </form>

          <button
            data-testid="forgot-password-btn"
            onClick={() => setShowForgot(true)}
            className="w-full text-center text-blue-300 text-sm mt-4 hover:text-blue-200 transition-colors"
          >
            {t('forgot_password')}
          </button>

          <div className="mt-6 pt-6 border-t border-white/10 text-center">
            <Link
              to="/admin"
              data-testid="admin-login-link"
              className="text-slate-400 text-xs hover:text-slate-300 transition-colors"
            >
              {t('admin_login')}
            </Link>
          </div>
        </div>
      </div>

      {/* Forgot Password Dialog */}
      <Dialog open={showForgot} onOpenChange={setShowForgot}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('reset_password')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>{t('enter_email')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  data-testid="forgot-email-input"
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="correo@ejemplo.com"
                  className="pl-10"
                />
              </div>
            </div>
            <Button
              data-testid="forgot-submit-btn"
              onClick={handleForgotPassword}
              className="w-full"
            >
              {t('reset_password')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
